import { ChevronLeftIcon, InfoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { UserInitialIcon } from '@/components/ui/user-initial-icon';

interface NarrativeHeaderProps {
  isRequestedView: boolean;
  onBack?: () => void;
  totalItems: number;
}

export function NarrativeHeader({ isRequestedView, onBack, totalItems }: NarrativeHeaderProps) {
  const { t } = useTranslation();
  const title = isRequestedView
    ? t('narrative.header.requestedTitle')
    : t('narrative.header.title');

  return (
    <div className="flex w-full shrink-0 items-center justify-between px-3 pt-2 pb-2">
      <div className="flex items-center gap-2">
        {isRequestedView && onBack && (
          <Button
            className="h-7 w-7 rounded-full"
            onClick={onBack}
            size="icon"
            type="button"
            variant="ghost"
          >
            <ChevronLeftIcon className="text-secondary h-4 w-4" />
          </Button>
        )}
        <div className="flex flex-col font-sans">
          <h2 className="text-foreground flex items-center gap-1 font-sans text-[15px] leading-[160%] font-medium tracking-normal">
            {title}
            <NarrativeHeaderInfoTooltip />
          </h2>
          <span className="text-neutral-mid text-xs">
            {t('narrative.header.items', { count: totalItems })}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <UserInitialIcon />
      </div>
    </div>
  );
}

function NarrativeHeaderInfoTooltip() {
  const { t } = useTranslation();

  return (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={t('narrative.header.infoAria')}
            className="hover:bg-elevated-subtle h-5 w-5 shrink-0 rounded-full bg-transparent p-0"
            size="icon"
            type="button"
            variant="ghost"
          >
            <InfoIcon className="text-neutral-mid h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{t('narrative.header.infoTooltip')}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
